import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import {
  getAllActiveSalesUsersByProjectId,
  getAllSalesUsers
} from '../../services/api/implementation/impl';

/**
 * Modal to pick a sales user for bulk pre-lead assignment.
 * POST body is built by the parent: { pre_lead_ids, sales_id }.
 */
export default function BulkAssignPreleadsModal({
  open,
  onClose,
  selectedCount = 0,
  projectId = null,
  isSubmitting = false,
  onConfirm
}) {
  const [salesId, setSalesId] = useState('');

  const projectIdNum = Number(projectId);
  const hasProject = Number.isFinite(projectIdNum) && projectIdNum > 0;

  const { data: salesUsersPayload, isLoading: isLoadingSales } = useQuery({
    queryKey: hasProject ? ['salesUsers', projectIdNum, 'active'] : ['salesUsers', 'all-active'],
    queryFn: () =>
      hasProject
        ? getAllActiveSalesUsersByProjectId(projectIdNum)
        : getAllSalesUsers(1, 500),
    enabled: open
  });

  const salesOptions = useMemo(() => {
    const list = Array.isArray(salesUsersPayload?.data)
      ? salesUsersPayload.data
      : Array.isArray(salesUsersPayload)
        ? salesUsersPayload
        : [];
    return list
      .filter((user) => user && (user.is_active === undefined || user.is_active))
      .map((user) => ({
        value: String(user.id),
        label: user.name || `User #${user.id}`
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [salesUsersPayload]);

  if (!open) return null;

  const handleClose = () => {
    if (isSubmitting) return;
    setSalesId('');
    onClose?.();
  };

  const handleConfirm = async () => {
    const id = Number(salesId);
    if (!Number.isFinite(id) || id <= 0) return;
    await onConfirm?.(id);
    setSalesId('');
  };

  return (
    <Modal name="Bulk assign pre-leads" onClose={handleClose} width={40}>
      <div className="space-y-4 pt-1">
        <p className="text-sm text-gray-600">
          Assign{' '}
          <span className="font-semibold text-gray-900">
            {selectedCount} pre-lead{selectedCount === 1 ? '' : 's'}
          </span>{' '}
          to a sales user.
        </p>

        <div>
          <label
            htmlFor="bulk-assign-sales-user"
            className="mb-1 block text-sm font-medium text-gray-700">
            Assign to
          </label>
          <select
            id="bulk-assign-sales-user"
            value={salesId}
            onChange={(e) => setSalesId(e.target.value)}
            disabled={isSubmitting || isLoadingSales}
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-customBlue focus:outline-none focus:ring-1 focus:ring-customBlue disabled:opacity-60">
            <option value="">
              {isLoadingSales ? 'Loading sales users…' : 'Select sales user'}
            </option>
            {salesOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {!isLoadingSales && salesOptions.length === 0 && (
            <p className="mt-1.5 text-xs text-amber-700">
              No active sales users found
              {hasProject ? ' for this project' : ''}.
            </p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" displayType="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isSubmitting || !salesId || salesOptions.length === 0}>
            {isSubmitting ? 'Assigning…' : 'Assign'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
